import { FC } from 'react';

import { Anchor, Group, Text } from '@mantine/core';
import { modals } from '@mantine/modals';

import { useAction } from 'hooks/use-action';
import { useInbox, useInboxFilterCounts, useInboxQuery } from 'hooks/use-inbox';
import { NotificationService } from 'services/NotificationService';

const InboxActions = () => {
    const { category, archived } = useInbox();
    const { categoryCounts } = useInboxFilterCounts();
    const query = useInboxQuery();

    const selection = category ? categoryCounts.byCategory[category] : categoryCounts.all;

    const [markAllAsRead, isMarkingAll] = useAction(() => NotificationService.readAll(query));
    const [archiveAll, isArchivingAll] = useAction(() => NotificationService.archiveAll(query));

    if (!selection.total || archived) {
        return null;
    }

    return (
        <Group gap="md" wrap="nowrap">
            {selection.unread > 0 && (
                <ActionLink busy={isMarkingAll} busyLabel="Marking…" onClick={markAllAsRead}>
                    Mark all read
                </ActionLink>
            )}
            <ActionLink
                busy={isArchivingAll}
                busyLabel="Archiving…"
                onClick={() =>
                    modals.openConfirmModal({
                        centered: true,
                        title: 'Archive all notifications?',
                        children: (
                            <Text size="sm">
                                This archives every notification matching your current filters. You can restore them
                                from the Archived view.
                            </Text>
                        ),
                        labels: { confirm: 'Archive', cancel: 'Cancel' },
                        onConfirm: () => archiveAll().then(),
                    })
                }
            >
                Archive all
            </ActionLink>
        </Group>
    );
};

const ActionLink: FC<{
    busy: boolean;
    busyLabel: string;
    onClick: () => unknown;
    children: React.ReactNode;
}> = ({ busy, busyLabel, onClick, children }) => (
    <Anchor component="button" fz="sm" fw={500} style={{ whiteSpace: 'nowrap' }} disabled={busy} onClick={onClick}>
        {busy ? busyLabel : children}
    </Anchor>
);

export { InboxActions };
